import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { FeatureGlyph } from '../features/FeatureMarkers';
import { FeaturesHelpButton } from '../features/FeaturesHelpGuide';
import {
  PaidFeatureBadge,
  PaidFeatureNote,
  isPaidAddOn,
} from '../features/PaidFeatureMark';
import HtmlEditor from './HtmlEditor';
import {
  createAdminCatalogFeature,
  deleteAdminCatalogFeature,
  displaySafeText,
  fetchAdminCatalog,
  fetchEntitlements,
  findEntitlement,
  getSelectedBoxId,
  isFeatureUsable,
  updateAdminCatalogFeature,
} from '../../utils/productLicensing';
import '../features/features.css';

const emptyForm = {
  feature_key: '',
  title: '',
  tooltip: '',
  body: '',
  included_in_base: false,
  talk_to_expert: true,
  is_active: true,
  sort_order: '',
};

const INCLUDE_HEADING = '<p><strong>What this includes</strong></p>';
const EXCLUDE_HEADING = '<p><strong>What this does not include</strong></p>';

const withSideBySideSections = (html) => {
  const body = String(html || '');
  if (!body || body.indexOf('feature-detail-sections') !== -1) {
    return body;
  }
  const includeIdx = body.indexOf(INCLUDE_HEADING);
  const excludeIdx = body.indexOf(EXCLUDE_HEADING);
  if (includeIdx === -1 || excludeIdx === -1 || excludeIdx <= includeIdx) {
    return body;
  }
  const intro = body.slice(0, includeIdx);
  const includes = body.slice(includeIdx, excludeIdx);
  const excludes = body.slice(excludeIdx);
  return (
    intro +
    '<div class="feature-detail-sections">' +
    '<div class="feature-detail-section">' +
    includes +
    '</div>' +
    '<div class="feature-detail-section">' +
    excludes +
    '</div>' +
    '</div>'
  );
};

const slugFromTitle = (title) =>
  String(title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'feature_$1')
    .slice(0, 64);

const catalogCardStatus = (feature, entitlement, t) => {
  if (feature.is_active === false) {
    return {
      className: 'is-locked',
      label: t('FeatureCatalog.Hidden', { defaultValue: 'Hidden' }),
    };
  }
  if (isFeatureUsable(entitlement)) {
    return {
      className: 'is-enabled',
      label: t('FeatureCatalog.Purchased', { defaultValue: 'Purchased' }),
    };
  }
  return {
    className: 'is-locked',
    label: t('FeatureCatalog.NotPurchased', { defaultValue: 'Not purchased' }),
  };
};

const fieldValue = (event) =>
  event && event.target ? event.target.value : '';

const fieldChecked = (event) =>
  !!(event && event.target && event.target.checked);

const htmlFromEditor = (html) => {
  if (html && typeof html === 'object' && html.target) {
    return String(html.target.value || '');
  }
  return html == null ? '' : String(html);
};

class EditorGuard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: '' };
  }

  componentDidCatch(error) {
    this.setState({
      error: (error && error.message) || 'The editor failed to load.',
    });
  }

  render() {
    if (this.state.error) {
      return (
        <p className="feature-catalog-dialog-hint">{this.state.error}</p>
      );
    }
    return this.props.children;
  }
}

const FeatureCatalogAdmin = ({ t }) => {
  const isIlocalUser = localStorage.getItem('isIlocalUser') === 'true';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [keyTouched, setKeyTouched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const boxId = getSelectedBoxId();
      const catalogData = await fetchAdminCatalog();
      setFeatures((catalogData && catalogData.features) || []);
      try {
        const entitlementData = await fetchEntitlements({ boxId });
        setEntitlements((entitlementData && entitlementData.entitlements) || []);
      } catch (_err) {
        setEntitlements([]);
      }
    } catch (err) {
      toast.error(
        (err && err.message) ||
          t('FeatureCatalog.LoadError', {
            defaultValue: 'Unable to load the feature catalog.',
          })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isIlocalUser) {
      load();
    } else {
      setLoading(false);
    }
  }, [isIlocalUser]);

  const filtered = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return features;
    return features.filter((feature) => {
      const title = String(feature.title || '').toLowerCase();
      const tooltip = String(feature.tooltip || '').toLowerCase();
      const key = String(feature.feature_key || '').toLowerCase();
      return title.includes(query) || tooltip.includes(query) || key.includes(query);
    });
  }, [features, searchText]);

  const openCreate = () => {
    setEditingKey('');
    setForm(emptyForm);
    setKeyTouched(false);
    setDialogOpen(true);
  };

  const openEdit = (feature) => {
    setEditingKey(feature.feature_key);
    setForm({
      feature_key: feature.feature_key,
      title: feature.title || '',
      tooltip: feature.tooltip || '',
      body: feature.body || '',
      included_in_base: feature.included_in_base === true,
      talk_to_expert: feature.talk_to_expert !== false,
      is_active: feature.is_active !== false,
      sort_order: feature.sort_order == null ? '' : String(feature.sort_order),
    });
    setKeyTouched(true);
    setDialogOpen(true);
  };

  const handleTitleChange = (value) => {
    setForm((current) => ({
      ...current,
      title: value,
      feature_key:
        editingKey || keyTouched ? current.feature_key : slugFromTitle(value),
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        feature_key: form.feature_key,
        title: form.title,
        tooltip: form.tooltip,
        body: form.body,
        included_in_base: form.included_in_base === true,
        talk_to_expert: form.talk_to_expert !== false,
        is_active: form.is_active !== false,
        sort_order: form.sort_order,
      };
      if (editingKey) {
        await updateAdminCatalogFeature(editingKey, payload);
        toast.success(
          t('FeatureCatalog.Updated', { defaultValue: 'Feature updated.' })
        );
      } else {
        await createAdminCatalogFeature(payload);
        toast.success(
          t('FeatureCatalog.Created', {
            defaultValue: 'Feature registered in the catalog.',
          })
        );
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(
        (err && err.message) ||
          t('FeatureCatalog.SaveError', {
            defaultValue: 'Unable to save this feature.',
          })
      );
    } finally {
      setSaving(false);
    }
  };

  const inUseMessage = (feature) => {
    const inUseCount = Number(feature && feature.purchased_count) || 0;
    if (inUseCount === 1) {
      return t('FeatureCatalog.DeleteInUseOne', {
        defaultValue:
          'You cannot delete this feature. It is in use by 1 pharmacy.',
      });
    }
    if (inUseCount > 1) {
      return t('FeatureCatalog.DeleteInUse', {
        defaultValue:
          'You cannot delete this feature. It is in use by {{count}} pharmacies.',
        count: inUseCount,
      });
    }
    if (feature && feature.included_in_base === true) {
      return t('FeatureCatalog.DeleteInUseBase', {
        defaultValue:
          'You cannot delete this feature. It is in use as part of the base product.',
      });
    }
    return '';
  };

  const requestDelete = (feature) => {
    if (!isIlocalUser || !feature || !feature.feature_key) {
      return;
    }
    const message = inUseMessage(feature);
    setDeleteDialog({
      feature,
      blocked: !!message,
      message,
    });
  };

  const closeDeleteDialog = () => {
    if (!deleting) {
      setDeleteDialog(null);
    }
  };

  const handleDelete = async () => {
    const feature = deleteDialog && deleteDialog.feature;
    if (!isIlocalUser || !feature || !feature.feature_key || deleteDialog.blocked) {
      return;
    }
    setDeleting(true);
    try {
      await deleteAdminCatalogFeature(feature.feature_key);
      toast.success(
        t('FeatureCatalog.Deleted', { defaultValue: 'Feature deleted.' })
      );
      if (editingKey === feature.feature_key) {
        setDialogOpen(false);
      }
      setDeleteDialog(null);
      await load();
    } catch (err) {
      const message =
        (err && err.message) ||
        t('FeatureCatalog.DeleteError', {
          defaultValue: 'Unable to delete this feature.',
        });
      if (/cannot delete this feature/i.test(message)) {
        setDeleteDialog({
          feature,
          blocked: true,
          message,
        });
      } else {
        toast.error(message);
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!isIlocalUser) {
    return (
      <div className="feature-catalog-admin">
        <p className="licensing-empty">
          {t('LicensingAdmin.Forbidden', {
            defaultValue: 'This page is only available to iLocal members.',
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="feature-catalog-admin">
      <div className="features-hero">
        <p className="features-hero-eyebrow">
          {t('FeatureCatalog.Eyebrow', { defaultValue: 'Licensing catalog' })}
        </p>
        <p className="features-intro">
          {t('FeatureCatalog.Intro', {
            defaultValue:
              'These features appear on Features and can be enabled per pharmacy after the offline sale. The badge shows whether this pharmacy has purchased the feature. Registering a feature adds it to the catalog. Product workflows such as Deliver still need a code change if the feature is not already wired.',
          })}
        </p>
        <div className="features-hero-meta">
          <span>
            {t('FeatureCatalog.Count', {
              defaultValue: '{{count}} features',
              count: features.length,
            })}
          </span>
        </div>
      </div>

      <div className="feature-catalog-toolbar">
        <TextField
          className="features-search-field"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(event) => setSearchText(fieldValue(event))}
          placeholder={t('FeatureCatalog.SearchPlaceholder', {
            defaultValue: 'Search catalog...',
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{
            textTransform: 'none',
            backgroundColor: '#00a7e1',
            borderRadius: '10px',
            fontWeight: 600,
            boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
            '&:hover': { backgroundColor: '#0096cc' },
          }}
        >
          {t('FeatureCatalog.Register', { defaultValue: 'Register feature' })}
        </Button>
        <FeaturesHelpButton t={t} />
      </div>

      {loading ? (
        <CircularProgress size={28} />
      ) : filtered.length === 0 ? (
        <div className="licensing-empty-card">
          <h3>
            {t('FeatureCatalog.EmptyTitle', {
              defaultValue: 'No features in the catalog',
            })}
          </h3>
          <p>
            {t('FeatureCatalog.Empty', {
              defaultValue: 'Register a feature to offer it for licensing.',
            })}
          </p>
        </div>
      ) : (
        <div className="features-grid">
          {filtered.map((feature) => {
            const status = catalogCardStatus(
              feature,
              findEntitlement(entitlements, feature.feature_key),
              t
            );
            return (
              <div
                key={feature.feature_key}
                role="button"
                tabIndex={0}
                className={`feature-card ${status.className}`}
                onClick={() => openEdit(feature)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openEdit(feature);
                  }
                }}
              >
                <div className="feature-card-header">
                  <span className={`feature-card-icon ${status.className}`}>
                    <FeatureGlyph featureKey={feature.feature_key} />
                  </span>
                  <span className="feature-card-header-marks">
                    {isPaidAddOn(feature) ? <PaidFeatureBadge t={t} /> : null}
                    <span className={`feature-card-status ${status.className}`}>
                      {status.label}
                    </span>
                  </span>
                </div>
                <h2 className="feature-card-title">
                  {displaySafeText(feature.title, feature.feature_key)}
                </h2>
                <p className="feature-card-tooltip">{feature.tooltip}</p>
                <div className="feature-catalog-card-meta">
                  <span>
                    {feature.wired
                      ? t('FeatureCatalog.Wired', {
                          defaultValue: 'Wired in product',
                        })
                      : t('FeatureCatalog.CatalogOnly', {
                          defaultValue: 'Catalog only',
                        })}
                  </span>
                  <span>
                    {t('FeatureCatalog.PurchasedCount', {
                      defaultValue: '{{count}} pharmacies',
                      count: feature.purchased_count || 0,
                    })}
                  </span>
                  {isIlocalUser ? (
                    <button
                      type="button"
                      className="feature-catalog-card-delete"
                      onClick={(event) => {
                        event.stopPropagation();
                        requestDelete(feature);
                      }}
                    >
                      {t('FeatureCatalog.Delete', { defaultValue: 'Delete' })}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullScreen
        PaperProps={{
          className: 'feature-catalog-dialog-paper',
        }}
      >
        <form onSubmit={handleSave} className="feature-catalog-dialog-form">
          <DialogTitle className="feature-catalog-dialog-title">
            <span>
              {editingKey
                ? t('FeatureCatalog.EditTitle', { defaultValue: 'Edit feature' })
                : t('FeatureCatalog.RegisterTitle', {
                    defaultValue: 'Register feature',
                  })}
            </span>
            <IconButton
              aria-label={t('FeatureCatalog.Cancel', { defaultValue: 'Cancel' })}
              onClick={() => !saving && setDialogOpen(false)}
              disabled={saving}
              size="small"
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent className="feature-catalog-dialog-content">
            <p className="feature-catalog-dialog-hint">
              {t('FeatureCatalog.DialogHint', {
                defaultValue:
                  'This registers the feature for Features, Talk to Expert, and Product Licensing. It does not charge pharmacies or automatically add a new Deliver workflow.',
              })}
            </p>
            <div className="feature-catalog-dialog-columns">
              <section className="feature-catalog-dialog-panel feature-catalog-dialog-input">
                <h3 className="feature-catalog-dialog-panel-title">
                  {t('FeatureCatalog.InputSection', { defaultValue: 'Input' })}
                </h3>
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  label={t('FeatureCatalog.Title', { defaultValue: 'Title' })}
                  value={form.title}
                  onChange={(event) => handleTitleChange(fieldValue(event))}
                />
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  label={t('FeatureCatalog.Code', { defaultValue: 'Feature code' })}
                  value={form.feature_key}
                  disabled={!!editingKey}
                  helperText={t('FeatureCatalog.CodeHelp', {
                    defaultValue:
                      'Lowercase letters, numbers, and underscores. This stays the same after you register it.',
                  })}
                  onChange={(event) => {
                    const featureKey = fieldValue(event);
                    setKeyTouched(true);
                    setForm((current) => ({
                      ...current,
                      feature_key: featureKey,
                    }));
                  }}
                />
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  multiline
                  minRows={4}
                  label={t('FeatureCatalog.Tooltip', {
                    defaultValue: 'Short description',
                  })}
                  value={form.tooltip}
                  onChange={(event) => {
                    const tooltip = fieldValue(event);
                    setForm((current) => ({ ...current, tooltip: tooltip }));
                  }}
                />
              </section>

              <section className="feature-catalog-dialog-panel feature-catalog-dialog-editor">
                <h3 className="feature-catalog-dialog-panel-title">
                  {t('FeatureCatalog.EditorSection', { defaultValue: 'Editor' })}
                </h3>
                {dialogOpen ? (
                  <EditorGuard>
                    <HtmlEditor
                      key={editingKey || 'new'}
                      fillHeight
                      label={t('FeatureCatalog.Body', {
                        defaultValue: 'Details shown on the feature page',
                      })}
                      value={form.body}
                      onChange={(html) => {
                        const body = htmlFromEditor(html);
                        setForm((current) => ({ ...current, body: body }));
                      }}
                    />
                  </EditorGuard>
                ) : null}
              </section>

              <section className="feature-catalog-dialog-panel feature-catalog-dialog-preview">
                <h3 className="feature-catalog-dialog-panel-title">
                  {t('FeatureCatalog.PreviewSection', { defaultValue: 'Preview' })}
                </h3>
                <div className="feature-catalog-preview">
                  <div className="feature-detail-hero feature-catalog-preview-hero">
                    <span
                      className={`feature-card-icon ${
                        form.is_active ? 'is-enabled' : 'is-locked'
                      }`}
                    >
                      <FeatureGlyph featureKey={form.feature_key || 'feature'} />
                    </span>
                    <div className="feature-detail-hero-copy">
                      <div className="feature-detail-header">
                        <h1 className="feature-detail-title">
                          {displaySafeText(form.title, form.feature_key || 'feature')}
                        </h1>
                        {form.included_in_base ? null : (
                          <PaidFeatureBadge t={t} />
                        )}
                      </div>
                      <span
                        className={`feature-card-status ${
                          form.is_active ? 'is-enabled' : 'is-locked'
                        }`}
                      >
                        {form.is_active
                          ? t('FeatureCatalog.Listed', { defaultValue: 'Listed' })
                          : t('FeatureCatalog.Hidden', { defaultValue: 'Hidden' })}
                      </span>
                    </div>
                  </div>
                  {form.tooltip ? (
                    <p className="feature-card-tooltip feature-catalog-preview-tooltip">
                      {form.tooltip}
                    </p>
                  ) : (
                    <p className="feature-catalog-preview-placeholder">
                      {t('FeatureCatalog.PreviewTooltipEmpty', {
                        defaultValue: 'Short description preview will appear here.',
                      })}
                    </p>
                  )}
                  {form.included_in_base ? null : <PaidFeatureNote t={t} />}
                  {form.body ? (
                    <div
                      className="feature-detail-body"
                      dangerouslySetInnerHTML={{
                        __html: withSideBySideSections(form.body),
                      }}
                    />
                  ) : (
                    <p className="feature-catalog-preview-placeholder">
                      {t('FeatureCatalog.PreviewBodyEmpty', {
                        defaultValue: 'Feature details preview will appear here.',
                      })}
                    </p>
                  )}
                </div>
              </section>
            </div>
          </DialogContent>
          <DialogActions className="feature-catalog-dialog-actions">
            <div className="feature-catalog-dialog-toggles">
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(event) => {
                      const checked = fieldChecked(event);
                      setForm((current) => ({
                        ...current,
                        is_active: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label={t('FeatureCatalog.ListedSwitch', {
                  defaultValue: 'Listed in catalog',
                })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.talk_to_expert}
                    onChange={(event) => {
                      const checked = fieldChecked(event);
                      setForm((current) => ({
                        ...current,
                        talk_to_expert: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label={t('FeatureCatalog.TalkToExpert', {
                  defaultValue: 'Show Talk to Expert',
                })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.included_in_base}
                    onChange={(event) => {
                      const checked = fieldChecked(event);
                      setForm((current) => ({
                        ...current,
                        included_in_base: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label={t('FeatureCatalog.Included', {
                  defaultValue: 'Included in base product',
                })}
              />
            </div>
            <div className="feature-catalog-dialog-buttons">
              {editingKey && isIlocalUser ? (
                <Button
                  type="button"
                  onClick={() =>
                    requestDelete(
                      features.find((item) => item.feature_key === editingKey) || {
                        feature_key: editingKey,
                        title: form.title,
                        purchased_count: 0,
                        included_in_base: form.included_in_base === true,
                      }
                    )
                  }
                  disabled={saving || deleting}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    color: '#b91c1c',
                    px: 2,
                    '&:hover': { backgroundColor: '#fef2f2' },
                  }}
                >
                  {t('FeatureCatalog.Delete', { defaultValue: 'Delete' })}
                </Button>
              ) : null}
              <Button
                onClick={() => setDialogOpen(false)}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  color: '#64748b',
                  px: 2,
                  '&:hover': { backgroundColor: '#f1f5f9' },
                }}
              >
                {t('FeatureCatalog.Cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                disableElevation
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#00a7e1',
                  borderRadius: '10px',
                  fontWeight: 600,
                  minWidth: 108,
                  px: 2.5,
                  py: 0.9,
                  boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
                  '&:hover': {
                    backgroundColor: '#0096cc',
                    boxShadow: '0 8px 18px rgba(0, 150, 204, 0.32)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#bae6fd',
                    color: '#ffffff',
                    boxShadow: 'none',
                  },
                }}
              >
                {saving
                  ? t('FeatureCatalog.Saving', { defaultValue: 'Saving...' })
                  : editingKey
                    ? t('FeatureCatalog.Save', { defaultValue: 'Save' })
                    : t('FeatureCatalog.Register', {
                        defaultValue: 'Register feature',
                      })}
              </Button>
            </div>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog
        open={!!deleteDialog}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'feature-catalog-confirm-paper' }}
      >
        <DialogTitle className="feature-catalog-confirm-title">
          <span>
            {deleteDialog && deleteDialog.blocked
              ? t('FeatureCatalog.DeleteBlockedTitle', {
                  defaultValue: 'Cannot delete feature',
                })
              : t('FeatureCatalog.DeleteTitle', {
                  defaultValue: 'Delete feature',
                })}
          </span>
          <IconButton
            aria-label={t('FeatureCatalog.Cancel', { defaultValue: 'Cancel' })}
            onClick={closeDeleteDialog}
            disabled={deleting}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className="feature-catalog-confirm-content">
          <p className="feature-catalog-confirm-body">
            {deleteDialog && deleteDialog.blocked
              ? deleteDialog.message
              : t('FeatureCatalog.DeleteConfirm', {
                  defaultValue:
                    'Delete {{title}}? This removes it from the catalog.',
                  title:
                    (deleteDialog &&
                      deleteDialog.feature &&
                      (deleteDialog.feature.title ||
                        deleteDialog.feature.feature_key)) ||
                    '',
                })}
          </p>
        </DialogContent>
        <DialogActions className="feature-catalog-confirm-actions">
          {deleteDialog && deleteDialog.blocked ? (
            <Button
              onClick={closeDeleteDialog}
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                backgroundColor: '#00a7e1',
                borderRadius: '10px',
                fontWeight: 600,
                minWidth: 108,
                px: 2.5,
                py: 0.9,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#0096cc' },
              }}
            >
              {t('FeatureCatalog.Close', { defaultValue: 'Close' })}
            </Button>
          ) : (
            <React.Fragment>
              <Button
                onClick={closeDeleteDialog}
                disabled={deleting}
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  color: '#00a7e1',
                  px: 2,
                  '&:hover': { backgroundColor: '#e8f6fc' },
                }}
              >
                {t('FeatureCatalog.Cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                variant="contained"
                disableElevation
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#00a7e1',
                  borderRadius: '10px',
                  fontWeight: 600,
                  minWidth: 108,
                  px: 2.5,
                  py: 0.9,
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#0096cc' },
                  '&.Mui-disabled': {
                    backgroundColor: '#bae6fd',
                    color: '#ffffff',
                  },
                }}
              >
                {deleting
                  ? t('FeatureCatalog.Deleting', { defaultValue: 'Deleting...' })
                  : t('FeatureCatalog.Delete', { defaultValue: 'Delete' })}
              </Button>
            </React.Fragment>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withTranslation()(FeatureCatalogAdmin);



// WEBPACK FOOTER //
// ./src/components/feature-catalog-admin/index.js